import { action, computed, observable } from 'mobx';
import ConfigRepository from '../../repositories/ConfigRepository';
import InvoiceRepository from '../../repositories/InvoiceRepository';
import InvoiceFormStore from '../../domain/entities/InvoiceFormStore';
import {
  IInvoiceDonationLoveCodeOption,
  IInvoiceCarrierTypeOption,
} from '../../domain/entities/Config';
import {
  FIELD_TYPE,
  INVOICE_CARRIER_TYPES,
  INVOICE_DONATE_MARKS,
} from '../../constants/Constants';
import CompanyBillingDetailsRepository from '../../repositories/CompanyBillingDetailsRepository';
import TransactionRepository from '../../repositories/TransactionRepository';
import Transaction from '../../domain/entities/Transaction/Transaction';

interface IInvoiceViewControllerData {
  formStore: InvoiceFormStore;
  invoiceCarrierTypeOptions: IInvoiceCarrierTypeOption[];
  invoiceDonationLoveCodeOptions: IInvoiceDonationLoveCodeOption[];
  isCarrierTypeMissing: boolean;
  isTransactionLoaded: boolean;
  isUpdating: boolean;
  selectedCarrierType: INVOICE_CARRIER_TYPES;
  selectedInvoiceType: INVOICE_DONATE_MARKS;
}

interface IInvoiceViewControllerConfig {
  companyBillingDetailsRepository: CompanyBillingDetailsRepository;
  configRepository: ConfigRepository;
  invoiceRepository: InvoiceRepository;
  transactionRepository: TransactionRepository;
}

export default class InvoiceViewController {
  private companyBillingDetailsRepository: CompanyBillingDetailsRepository;

  private configRepository: ConfigRepository;

  private invoiceRepository: InvoiceRepository;

  private transactionRepository: TransactionRepository;

  @observable transaction?: Transaction;

  constructor({
    companyBillingDetailsRepository,
    configRepository,
    invoiceRepository,
    transactionRepository,
  }: IInvoiceViewControllerConfig) {
    this.companyBillingDetailsRepository = companyBillingDetailsRepository;
    this.configRepository = configRepository;
    this.invoiceRepository = invoiceRepository;
    this.transactionRepository = transactionRepository;
  }

  async loaded(): Promise<void> {
    const transaction = await this.transactionRepository.getTransaction();
    this.transaction = transaction;
    if (transaction.invoiceDetails) {
      this.invoiceRepository.setInvoiceDetails(transaction.invoiceDetails);
    }
    this.invoiceRepository.setCompanyBillingDetailsFormStore(
      this.companyBillingDetailsRepository.formStore,
    );
  }

  @computed
  public get invoiceFormStore(): InvoiceFormStore {
    return this.invoiceRepository.invoiceFormStore;
  }

  @computed
  public get data(): IInvoiceViewControllerData {
    const { invoiceDonationLoveCodeOptions, invoiceCarrierTypeOptions } =
      this.configRepository.config;

    const selectedInvoiceType =
      this.invoiceFormStore.values[FIELD_TYPE.INVOICE_DONATE_MARK];

    const selectedCarrierType =
      this.invoiceFormStore.values[FIELD_TYPE.INVOICE_CARRIER_TYPE];
    const isCarrierTypeMissing =
      selectedInvoiceType === INVOICE_DONATE_MARKS.ELECTRONIC_INVOICE &&
      selectedCarrierType === INVOICE_CARRIER_TYPES.NONE;

    return {
      formStore: this.invoiceFormStore,
      invoiceCarrierTypeOptions,
      invoiceDonationLoveCodeOptions,
      isCarrierTypeMissing,
      isTransactionLoaded: !!this.transaction?.isTransactionLoaded,
      isUpdating: this.invoiceRepository.isUpdating,
      selectedCarrierType,
      selectedInvoiceType,
    };
  }

  @action
  selectInvoiceType = (invoiceType: INVOICE_DONATE_MARKS): void => {
    this.invoiceFormStore.setFieldValue(
      FIELD_TYPE.INVOICE_DONATE_MARK,
      invoiceType,
    );
  };

  @action
  selectCarrierType = (carrierType: INVOICE_CARRIER_TYPES): void => {
    this.invoiceFormStore.setFieldValue(
      FIELD_TYPE.INVOICE_CARRIER_TYPE,
      carrierType,
    );
  };
}
